import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete.tsx';
import type {
  ContextualSegmentsDrawerState,
  ContextualSegmentsV2FormState,
} from '@openx/types/targeting/advancedTargeting.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkAddDrawer } from '../../shared/BulkAdd/BulkAddDrawer.tsx';
import { comparisonOptionLabels, filteredTypeOptions, getBulkPlaceholder } from './constants.ts';
import { validateBulkContextualSegments } from './utils.ts';

type ContextualSegmentsBulkV2Props = {
  isOpen: boolean;
  closeDrawer: () => void;
  updateParams: Dispatch<SetStateAction<ContextualSegmentsDrawerState>>;
  contextualItems: ContextualSegmentsV2FormState;
};

export const ContextualSegmentsBulkV2 = ({
  isOpen,
  closeDrawer,
  contextualItems,
  updateParams,
}: ContextualSegmentsBulkV2Props) => {
  const { t } = useTranslation();
  const [comparisonType, setComparisonType] = useState<
    typeof ComparisonType.INTERSECTS | typeof ComparisonType.NOT_INTERSECTS
  >(ComparisonType.INTERSECTS);
  const optionLabels = useMemo(() => comparisonOptionLabels(), []);
  const addBulkHandler = useCallback(
    (addedItems: string[]) => {
      const inclusionKey = comparisonType === ComparisonType.INTERSECTS ? 'includes' : 'excludes';

      updateParams(prevState => {
        const alreadyOnTheList = prevState.contextualV2 ? prevState.contextualV2[inclusionKey] : [];

        return {
          ...prevState,
          contextualV2: {
            ...prevState.contextualV2,
            [inclusionKey]: [...alreadyOnTheList, ...addedItems],
            op: comparisonType,
          },
        };
      });

      closeDrawer();
    },
    [closeDrawer, comparisonType, updateParams]
  );

  return (
    isOpen && (
      <BulkAddDrawer
        onClose={closeDrawer}
        onApply={addBulkHandler}
        placeholder={getBulkPlaceholder()}
        validate={items => validateBulkContextualSegments(items, contextualItems)}
      >
        <AutoComplete
          options={filteredTypeOptions}
          renderOptions={{
            dataTest: 'comparison-option',
          }}
          getOptionLabel={option => optionLabels[option] ?? ''}
          textFieldReadOnly
          disableClearable
          data-test={'comparison-options'}
          textFieldProps={{
            label: t('Comparison Type'),
            placeholder: t('Select Type'),
          }}
          value={comparisonType}
          onChange={(_, value) => {
            setComparisonType(value);
          }}
        />
      </BulkAddDrawer>
    )
  );
};
